import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import {
  BarChart3,
  ClipboardCheck,
  FileText,
  Images,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  MonitorPlay,
  Users,
} from 'lucide-react';
import { SuiteShell, APP_ACCENTS, type SuiteAppConfig, type SuiteNavItem } from '@vmlmap/shared-ui';

import { AccessPendingScreen } from '@/components/AccessPendingScreen';
import { ParticlesBackground } from '@/components/layout/ParticlesBackground';
import { SidebarProvider, useSidebar } from '@/components/ui/sidebar';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useCurrentUser } from '@/hooks/useUserRole';
import { useCanOpenAssetLibrary, useCanOpenContentTracker } from '@/hooks/useAppAccess';
import { useCanManageUsers } from '@/pages/users/components/useUserManagement';
import { usePermissions } from '@/hooks/usePermissions';
import { useAssetLibraryPermissions } from '@/pages/asset-library/useAssetLibraryPermissions';
import { isNoRoleError } from '@/utils/errors';

import { SuiteUserMenu } from './SuiteUserMenu';

const HUB_ID = 'hub';
const CONTENT_TRACKER_ID = 'tracker';
const ASSET_LIBRARY_ID = 'asset';

/** Route prefix owned by Asset Library. */
const ASSET_LIBRARY_PATH = '/assets';
const MY_BRIEFS_PATH = '/assets/briefs';

/** WPP Open wordmark + suite descriptor, matching the suite mockup. */
function SuiteLogo() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#0014cc] text-[10px] font-extrabold tracking-[0.5px] text-white">
        WPP
      </div>
      <span className="text-[15px] font-bold tracking-[-0.2px] text-slate-800">
        Open <span className="font-normal text-slate-400">· MAP Suite</span>
      </span>
    </div>
  );
}

/**
 * Content Tracker's adapter for the shared {@link SuiteShell}.
 *
 * Builds two apps (Suite Home + Content Tracker), derives the active app / nav /
 * accent from the current route, and wires the switcher to navigate. Runs only
 * when the `suiteShell` flag is on; the legacy {@link AppShell} stays the
 * default and the rollback route.
 */
export function SuiteAppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: canManageUsers } = useCanManageUsers();
  const { allowed: canOpenAssetLibrary } = useCanOpenAssetLibrary();
  const { permissions: assetLibraryPermissions } = useAssetLibraryPermissions({
    enabled: canOpenAssetLibrary,
  });
  const { allowed: canOpenContentTracker } = useCanOpenContentTracker();
  const { data: permissions } = usePermissions();

  // Suite-level routes: the hub (and any nested hub routes) plus suite-wide
  // admin (user/permission panel).
  const onSuiteHome =
    location.pathname === '/suite' ||
    location.pathname.startsWith('/suite/') ||
    location.pathname.startsWith('/admin/users');

  // Exact match or a `/assets/...` child, so a future `/assets-something`
  // route can't be misattributed to Asset Library. Users without the grant
  // never resolve to that app, so the switcher can never show a current app
  // that isn't in its own list.
  const onAssetLibrary =
    canOpenAssetLibrary &&
    (location.pathname === ASSET_LIBRARY_PATH ||
      location.pathname.startsWith(`${ASSET_LIBRARY_PATH}/`));

  const hubNav: SuiteNavItem[] = [
    { title: 'Overview', path: '/suite', icon: <LayoutGrid className="h-4 w-4" /> },
    // Users lives at suite level: permissions for all apps will be managed here
    // (per-app access columns to be added to the panel later).
    ...(canManageUsers
      ? [
          {
            title: 'Users',
            path: '/admin/users',
            icon: <Users className="h-4 w-4" />,
          } as SuiteNavItem,
        ]
      : []),
  ];

  const trackerNav: SuiteNavItem[] = [
    {
      title: 'Content Tracker',
      path: '/content-tracker',
      icon: <BarChart3 className="h-4 w-4" />,
      matchPrefixes: ['/campaigns/'],
    },
    {
      title: 'Plan Approval',
      path: '/media-plan-approval',
      icon: <ClipboardCheck className="h-4 w-4" />,
    },
    { title: 'Help & Support', path: '/about', icon: <LifeBuoy className="h-4 w-4" /> },
    // Dashboard visibility is backend-driven (admins only)
    ...(permissions?.can_view_dashboard
      ? [
          {
            title: 'Dashboard',
            path: '/dashboard',
            icon: <LayoutDashboard className="h-4 w-4" />,
          } as SuiteNavItem,
        ]
      : []),
    ...(canManageUsers
      ? ([
          { title: 'Mockups', path: '/mockups', icon: <MonitorPlay className="h-4 w-4" /> },
        ] satisfies SuiteNavItem[])
      : []),
  ];

  const assetLibraryNav: SuiteNavItem[] = [
    {
      title: 'Assets',
      path: ASSET_LIBRARY_PATH,
      icon: <Images className="h-4 w-4" />,
    },
    // Only for roles that brief: anyone else would find an empty page.
    ...(assetLibraryPermissions.can_brief
      ? [
          {
            title: 'My Briefs',
            path: MY_BRIEFS_PATH,
            icon: <FileText className="h-4 w-4" />,
          } as SuiteNavItem,
        ]
      : []),
  ];

  const hubApp: SuiteAppConfig = {
    id: HUB_ID,
    name: 'Suite Home',
    subtitle: 'All apps & activity',
    accent: APP_ACCENTS.hub,
    iconLabel: '⌂',
    navItems: hubNav,
  };

  const trackerApp: SuiteAppConfig = {
    id: CONTENT_TRACKER_ID,
    name: 'Content Tracker',
    subtitle: 'Social & search delivery',
    accent: APP_ACCENTS.tracker,
    iconLabel: 'CT',
    description: 'Track and manage campaign content across the suite.',
    navItems: trackerNav,
  };

  const assetLibraryApp: SuiteAppConfig = {
    id: ASSET_LIBRARY_ID,
    name: 'Asset Library',
    subtitle: 'Asset management',
    accent: APP_ACCENTS.asset,
    iconLabel: 'AL',
    description: 'Upload, tag and publish Ford assets.',
    navItems: assetLibraryNav,
  };

  // Order matters: the hub owns `/suite` and `/admin/users`, Asset Library owns
  // `/assets`, and Content Tracker is the fallback because it owns `/`.
  //
  // Without the Content Tracker grant that fallback would name an app the
  // switcher doesn't list and whose pages redirect away, so the hub takes over
  // as the fallback instead — the user's remaining suite-level surface.
  const currentAppId = onSuiteHome
    ? HUB_ID
    : onAssetLibrary
      ? ASSET_LIBRARY_ID
      : canOpenContentTracker
        ? CONTENT_TRACKER_ID
        : HUB_ID;
  const navItems = onSuiteHome
    ? hubNav
    : onAssetLibrary
      ? assetLibraryNav
      : canOpenContentTracker
        ? trackerNav
        : hubNav;

  const handleSelectApp = (appId: string) => {
    const destination =
      appId === HUB_ID
        ? '/suite'
        : appId === ASSET_LIBRARY_ID
          ? ASSET_LIBRARY_PATH
          : '/content-tracker';
    navigate({ to: destination });
  };

  return (
    <SidebarProvider defaultOpen>
      <SuiteShellBody
        // Each app appears only for users who hold its grant. This hides the
        // door; the lock is server-side in each app's API.
        apps={[
          hubApp,
          ...(canOpenContentTracker ? [trackerApp] : []),
          ...(canOpenAssetLibrary ? [assetLibraryApp] : []),
        ]}
        currentAppId={currentAppId}
        onSelectApp={handleSelectApp}
        navItems={navItems}
        pathname={location.pathname}
      />
    </SidebarProvider>
  );
}

/**
 * Inner shell body — lives inside {@link SidebarProvider} so it can read the
 * shared `useSidebar()` collapse state (toggled by the Breadcrumb button) and
 * drive the suite sidebar's collapsed width.
 */
function SuiteShellBody({
  apps,
  currentAppId,
  onSelectApp,
  navItems,
  pathname,
}: {
  apps: SuiteAppConfig[];
  currentAppId: string;
  onSelectApp: (appId: string) => void;
  navItems: SuiteNavItem[];
  pathname: string;
}) {
  const { state } = useSidebar();

  return (
    <SuiteShell
      apps={apps}
      currentAppId={currentAppId}
      onSelectApp={onSelectApp}
      navItems={navItems}
      pathname={pathname}
      logo={<SuiteLogo />}
      userMenu={<SuiteUserMenu />}
      background={<ParticlesBackground />}
      sidebarCollapsed={state === 'collapsed'}
    >
      <Outlet />
    </SuiteShell>
  );
}

/**
 * Gate that reproduces {@link AppShell}'s user-loading / error / no-role states,
 * then renders the suite shell. Kept separate so the loading UX is identical
 * across the flag boundary.
 */
export function SuiteAppShellGate() {
  useUserProfile();
  const { error: userError, isLoading: userLoading, refetch } = useCurrentUser();

  if (userLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-slate-400" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (userError && isNoRoleError(userError)) {
    return <AccessPendingScreen />;
  }

  if (userError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="mb-2 text-xl font-semibold text-slate-900">Something went wrong</h2>
          <p className="mb-6 text-slate-600">
            We couldn&apos;t load your account information. Please try again.
          </p>
          <button
            onClick={() => refetch()}
            className="rounded-lg bg-slate-900 px-4 py-2 text-white transition-colors hover:bg-slate-800"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return <SuiteAppShell />;
}
